import { useTranslation } from "react-i18next";
import type { Trip, ThemeClasses } from "../types";
import {
  getStatusColor,
  getStatusStyle,
  getStatusLabel,
  getPriorityColor,
  getPriorityLabel,
} from "../utils";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { DENSE_ROW, ICON_BTN, ICON_BTN_DANGER, STATUS_CHIP } from "../../../../shared";

interface TripListRowProps {
  trip: Trip;
  themeClasses: ThemeClasses;
  onEdit: (trip: Trip) => void;
  onDetails: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
}

export function TripListRow({
  trip,
  themeClasses,
  onEdit,
  onDetails,
  onDelete,
}: TripListRowProps) {
  const { t } = useTranslation("adminSections");
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();

  const assignmentsCount = trip.assignments?.length || 0;
  const activeAssignments =
    trip.assignments?.filter(
      (a) => a.status === "active" || a.status === "pending",
    ).length || 0;

  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`;
  const title = trip.title || t("trips.untitled");
  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
        <i className="ri-route-line text-base"></i>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={title}>
            {title}
          </h3>
          {trip.status?.name && (
            <span
              className={`${STATUS_CHIP} ${getStatusColor(trip)}`}
              style={getStatusStyle(trip)}
            >
              {getStatusLabel(trip, t)}
            </span>
          )}
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {trip.route?.name || "N/A"}
        </p>
      </div>
      <div className="hidden md:block w-24 shrink-0 min-w-0">
        <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>
          {t("trips.assignments.title")}
        </p>
        <p className={`text-sm font-semibold ${themeClasses.text} tabular-nums`}>
          {activeAssignments}/{assignmentsCount}
        </p>
      </div>
      <div className="hidden sm:flex w-20 shrink-0 justify-start">
        {trip.priority && (
          <span className={`${STATUS_CHIP} ${getPriorityColor(trip.priority)}`}>
            {getPriorityLabel(trip.priority, t)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {!isMonitor && !isReadOnly && (
          <>
            <CRUDTooltip>
              <button
                className={neutralBtn}
                onClick={() => onEdit(trip)}
                disabled={disableCRUD}
                aria-label={t("trips.actions.edit", "Editar")}
                title={t("trips.actions.edit", "Editar")}
              >
                <i className="ri-edit-line"></i>
              </button>
            </CRUDTooltip>
            <CRUDTooltip>
              <button
                onClick={() => onDelete(trip)}
                disabled={disableCRUD}
                className={ICON_BTN_DANGER}
                aria-label={t("trips.actions.delete", "Eliminar")}
                title={t("trips.actions.delete", "Eliminar")}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </CRUDTooltip>
          </>
        )}
        <button
          className={neutralBtn}
          onClick={() => onDetails(trip)}
          aria-label={t("trips.actions.details", "Detalles")}
          title={t("trips.actions.details", "Detalles")}
        >
          <i className="ri-eye-line"></i>
        </button>
      </div>
    </div>
  );
}
